import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bus, Route, User, AlertCircle, CheckCircle } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

const DriverAssignmentModal = ({ isOpen, onClose, driver, onSubmit, onUnassign }) => {
  const [selectedBusId, setSelectedBusId] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [buses, setBuses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Charger les buses et routes disponibles
  const loadData = async () => {
    try {
      setLoading(true);
      const [busesResponse, routesResponse] = await Promise.all([
        unifiedApi.buses.getAll(),
        unifiedApi.routes.getAll(),
      ]);

      setBuses(busesResponse.data || []);
      setRoutes(routesResponse.data || []);
    } catch (err) {
      logger.error('Erreur chargement données assignation:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && driver) {
      setSelectedBusId(driver.assigned_bus_id || driver.assignedBusId || '');
      setSelectedRouteId(driver.assigned_route_id || driver.assignedRouteId || '');
      setError('');
    }
  }, [isOpen, driver]);

  const handleAssign = async () => {
    logger.debug('🚗 [DriverAssignmentModal] Début assignation chauffeur', {
      driverId: driver?.id,
      driverName: driver?.name || driver?.first_name + ' ' + driver?.last_name,
      selectedBusId,
      selectedRouteId,
      driver: driver,
    });

    if (!selectedBusId) {
      logger.debug('❌ [DriverAssignmentModal] Erreur: Aucun bus sélectionné');
      setError('Veuillez sélectionner un bus');
      return;
    }

    logger.debug('🔄 [DriverAssignmentModal] Appel de la fonction onSubmit...');
    setIsSubmitting(true);
    setError('');

    try {
      logger.debug('📤 [DriverAssignmentModal] Envoi assignation avec:', {
        driverId: driver.id,
        busId: selectedBusId,
        routeId: selectedRouteId,
      });

      await onSubmit(driver.id, selectedBusId, selectedRouteId);

      logger.debug('✅ [DriverAssignmentModal] Assignation réussie');
      onClose();
    } catch (err) {
      logger.error("❌ [DriverAssignmentModal] Erreur lors de l'assignation:", err);
      logger.error("❌ [DriverAssignmentModal] Détails de l'erreur:", {
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError(`Erreur lors de l'assignation: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      await onUnassign(driver.id);
      onClose();
    } catch {
      setError('Erreur lors de la désassignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !driver) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Assignation Bus & Circuit</h2>
                <p className="text-sm text-gray-500">
                  {driver.first_name || driver.firstName} {driver.last_name || driver.lastName} (
                  {driver.ref || driver.id})
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Assignation actuelle */}
            {(driver.assigned_bus_id || driver.assignedBusId) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Assignation actuelle</h3>
                <div className="space-y-1 text-sm text-blue-700">
                  <div className="flex items-center space-x-2">
                    <Bus className="w-4 h-4" />
                    <span>Bus: {driver.assigned_bus_id || driver.assignedBusId}</span>
                  </div>
                  {(driver.assigned_route_id || driver.assignedRouteId) && (
                    <div className="flex items-center space-x-2">
                      <Route className="w-4 h-4" />
                      <span>Circuit: {driver.assigned_route_id || driver.assignedRouteId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Chargement des données...</p>
              </div>
            )}

            {/* Sélection du bus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner un bus</label>
              <select
                value={selectedBusId}
                onChange={(e) => setSelectedBusId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting || loading}
              >
                <option value="">-- Sélectionner un bus --</option>
                {buses.map((bus) => (
                  <option key={bus.id} value={bus.id}>
                    {bus.license_plate || bus.plateNumber} - {bus.make || bus.brand} {bus.model} ({bus.capacity} places)
                  </option>
                ))}
              </select>
            </div>

            {/* Sélection du circuit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un circuit (optionnel)
              </label>
              <select
                value={selectedRouteId}
                onChange={(e) => setSelectedRouteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting || loading}
              >
                <option value="">-- Aucun circuit --</option>
                {routes.map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.route_name || route.name} - {route.description || route.route_code || route.code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>

            {(driver.assigned_bus_id || driver.assignedBusId) && (
              <button
                onClick={handleUnassign}
                disabled={isSubmitting || loading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Désassignation...' : 'Désassigner'}
              </button>
            )}

            <button
              onClick={handleAssign}
              disabled={isSubmitting || !selectedBusId || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span>{isSubmitting ? 'Assignation...' : 'Assigner'}</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DriverAssignmentModal;
